/**
 * Fuel stock at the field base: TJR's Fuel page "Fuel Status" (Truck and Drums, AVGAS and AVTUR: Initial Fill, Last
 * Dip, Calc Rem) from the fuel ledger and the daily entries' refuels (TJR inventory 02 §8,
 * `decomp/FUN_0070eb38_0070eb38.c`).
 *
 * TJR's rule, kept exactly:
 *   - A ledger row records the stock on hand: a resupply ("Initial Fill") is what the truck or the drums hold after
 *     loading, a dip ("Last Dip") what was measured. Neither is added to the stock before it.
 *   - For each store (truck, drums) the starting row is the latest resupply and the latest dip that have any value
 *     for that store (avgas or turbine above 0, `FUN_0070ea68`); the later of the two wins, a dip on a tie. The row
 *     sets both fuel types of the store, so a truck row with turbine only sets truck avgas to 0.
 *   - Refuels taken off are the refuel amounts (`fuel_load_l`) of daily entries whose source is Truck or Drum, timed
 *     at the entry's date and take-off time (00:00 without one), at or after the starting row, split by the entry
 *     aircraft's fuel type. Bowser refuels never touch the stock.
 *
 * Teslyn changes (stated, not silent):
 *   - Drums (MB, Q130 A, 18 Oct 2026): drums stay a count; a drum refuel takes off
 *     litres ÷ litres per drum (`Project.fuel_drum_l`, 200 L by default). TJR took litres off the drum count.
 *   - TJR shows 0 when the calculation goes below zero; Teslyn shows the negative figure with a warning, so a
 *     missing resupply or a typing mistake is visible.
 *   - A refuel from an aircraft with no fuel type cannot be split; it is listed in a warning instead of being taken
 *     off avgas (what TJR's lookup falls back to).
 *   - A resupply also records the amount uplifted (MB, decision 159, Q138): the new total stays the figure that
 *     sets the stock, as TJR; the uplift is kept beside it and shown with the stock before it, so the two can be
 *     checked. TJR's resupply row is the total, unchanged.
 *   - Stock is kept per store and per location (MB, decision 160, Q139): `fuelStockByLocation` runs the same rule
 *     on each location's records and refuels. `fuelStock` over every location is TJR's combined Fuel Status, kept
 *     for the TJR figures.
 */
import { aircraftFuelType, chronological } from './aircraft.js';
import type { Aircraft, DailyEntry, FuelLedgerEntry, FuelType, Project, Uuid } from './model.js';

/** Litres in a drum when the project has no setting (MB, Q130 A). */
export const DEFAULT_DRUM_L = 200;

export type FuelStore = 'truck' | 'drums';
export const FUEL_STORES: readonly FuelStore[] = ['truck', 'drums'];
export const FUEL_TYPES: readonly FuelType[] = ['avgas', 'turbine'];

/** Words for the screens and reports ("Truck AVTUR"). TJR prints AVGAS and AVTUR. */
export const FUEL_TYPE_LABEL: Readonly<Record<FuelType, string>> = { avgas: 'AVGAS', turbine: 'AVTUR' };
export const FUEL_STORE_LABEL: Readonly<Record<FuelStore, string>> = { truck: 'Truck', drums: 'Drums' };

/** The project's litres per drum: its setting, else 200 L. */
export function drumLitres(project: Pick<Project, 'fuel_drum_l'> | null | undefined): number {
  const v = project?.fuel_drum_l;
  return v != null && Number.isFinite(v) && v > 0 ? v : DEFAULT_DRUM_L;
}

/** The ledger value of one store and fuel type: litres for the truck, a drum count for drums. */
export function ledgerValue(row: FuelLedgerEntry, store: FuelStore, fuel: FuelType): number {
  if (store === 'truck') return fuel === 'avgas' ? row.truck_avgas_l : row.truck_turbine_l;
  return fuel === 'avgas' ? row.drums_avgas : row.drums_turbine;
}

/** What a resupply delivered to one store and fuel type (decision 159), or null when it was not recorded. */
export function upliftValue(row: FuelLedgerEntry, store: FuelStore, fuel: FuelType): number | null {
  const v =
    store === 'truck'
      ? fuel === 'avgas'
        ? row.uplift_truck_avgas_l
        : row.uplift_truck_turbine_l
      : fuel === 'avgas'
        ? row.uplift_drums_avgas
        : row.uplift_drums_turbine;
  return v === undefined || v === null || !Number.isFinite(v) ? null : v;
}

/** True when a resupply records any amount uplifted. */
export function hasUplift(row: FuelLedgerEntry): boolean {
  return FUEL_STORES.some((st) => FUEL_TYPES.some((f) => upliftValue(row, st, f) !== null));
}

const touches = (row: FuelLedgerEntry, store: FuelStore): boolean =>
  ledgerValue(row, store, 'avgas') > 0 || ledgerValue(row, store, 'turbine') > 0;

const storeOf = (e: DailyEntry): FuelStore | null =>
  e.fuel_source === 'truck' ? 'truck' : e.fuel_source === 'drum' ? 'drums' : null;

/** `YYYY-MM-DD HH:MM` of a ledger row or entry, so text order is time order. */
const stamp = (date: string, time: string | null): string =>
  `${date} ${time !== null && /^\d{2}:\d{2}$/.test(time) ? time : '00:00'}`;

/** A ledger row that set the stock. */
export interface StockMark {
  readonly entry_id: Uuid;
  readonly date: string;
  readonly time: string;
  readonly location: string;
  /** Litres (truck) or drums. */
  readonly amount: number;
}

/** One stock card: a store and fuel type, as TJR's Fuel Status cell group. */
export interface FuelStockCard {
  readonly store: FuelStore;
  readonly fuel: FuelType;
  /** `L` for the truck, `drums` for drums. */
  readonly unit: 'L' | 'drums';
  /** TJR "Initial Fill": the latest resupply with a value for this store. */
  readonly last_fill: StockMark | null;
  /** TJR "Last Dip". */
  readonly last_dip: StockMark | null;
  /** The row the calculation starts from (the later of the two; a dip on a tie). */
  readonly from: 'fill' | 'dip' | null;
  /** Litres refuelled from this stock since that row. */
  readonly used_l: number;
  /** TJR "Calc Rem", in the card's unit; below zero when more was drawn than recorded. */
  readonly remaining: number;
  /** The remaining stock in litres (drums × litres per drum). */
  readonly remaining_l: number;
  /** Something has been recorded or drawn for this store and fuel type. */
  readonly in_use: boolean;
}

/** One line of the fuel history with the running balance after it. */
export interface FuelHistoryRow {
  readonly key: string;
  readonly date: string;
  readonly time: string;
  readonly kind: 'resupply' | 'dip' | 'refuel';
  readonly location: string;
  /** Refuels only: the entry, its aircraft and the litres drawn. */
  readonly daily_entry_id?: Uuid;
  readonly aircraft_id?: Uuid | null;
  readonly flt_num?: number | null;
  readonly store?: FuelStore;
  readonly fuel?: FuelType | null;
  readonly litres?: number;
  /** Balance of every store and fuel type after this row (litres for the truck, drums for drums). */
  readonly balance: Readonly<Record<FuelStore, Readonly<Record<FuelType, number>>>>;
  /**
   * Resupplies that record the amount uplifted (decision 159): per store and fuel type the uplift, the stock just
   * before and what the two add up to, beside the total the crew recorded (which set the stock, as TJR).
   */
  readonly uplift?: readonly UpliftCheck[];
}

export interface UpliftCheck {
  readonly store: FuelStore;
  readonly fuel: FuelType;
  readonly uplifted: number;
  readonly before: number;
  /** before + uplifted */
  readonly expected: number;
  /** The new total recorded. */
  readonly recorded: number;
}

export interface FuelStockWarning {
  readonly code: 'below_zero' | 'no_fuel_type' | 'no_stock' | 'no_location';
  readonly message: string;
}

export interface FuelStock {
  readonly drum_l: number;
  readonly cards: readonly FuelStockCard[];
  /** Oldest first. */
  readonly history: readonly FuelHistoryRow[];
  readonly warnings: readonly FuelStockWarning[];
}

type Balance = Record<FuelStore, Record<FuelType, number>>;
const zero = (): Balance => ({ truck: { avgas: 0, turbine: 0 }, drums: { avgas: 0, turbine: 0 } });
const copy = (b: Balance): Balance => ({ truck: { ...b.truck }, drums: { ...b.drums } });

function latest(rows: readonly FuelLedgerEntry[], kind: FuelLedgerEntry['kind'], store: FuelStore) {
  let best: FuelLedgerEntry | null = null;
  for (const r of rows) {
    if (r.kind !== kind || !touches(r, store)) continue;
    if (best === null || stamp(r.date, r.time) >= stamp(best.date, best.time)) best = r;
  }
  return best;
}

const fmtL = (n: number): string => Math.round(n).toLocaleString('en-AU');
const fmtDrums = (n: number): string => (Math.round(n * 10) / 10).toLocaleString('en-AU');

/**
 * Fuel stock per store and fuel type from the ledger and the daily entries. `aircraft` gives each entry's fuel type;
 * `drumL` is litres per drum (`drumLitres(project)`).
 */
export function fuelStock(
  ledger: readonly FuelLedgerEntry[],
  entries: readonly DailyEntry[],
  aircraft: readonly Aircraft[],
  drumL: number = DEFAULT_DRUM_L,
): FuelStock {
  const rows = ledger.filter((r) => r.deleted_at === null);
  const fuelOf = new Map(aircraft.map((a) => [a.id, aircraftFuelType(a)]));
  const regoOf = new Map(aircraft.map((a) => [a.id, a.registration]));
  const draws = chronological(
    entries.filter((e) => e.deleted_at === null && storeOf(e) !== null && e.fuel_load_l > 0),
  );

  // Starting row per store (TJR: the later of the latest fill and the latest dip, a dip on a tie).
  const start = new Map<FuelStore, { row: FuelLedgerEntry; from: 'fill' | 'dip' } | null>();
  const fills = new Map<FuelStore, FuelLedgerEntry | null>();
  const dips = new Map<FuelStore, FuelLedgerEntry | null>();
  for (const store of FUEL_STORES) {
    const fill = latest(rows, 'resupply', store);
    const dip = latest(rows, 'dip', store);
    fills.set(store, fill);
    dips.set(store, dip);
    if (dip && (!fill || stamp(dip.date, dip.time) >= stamp(fill.date, fill.time)))
      start.set(store, { row: dip, from: 'dip' });
    else if (fill) start.set(store, { row: fill, from: 'fill' });
    else start.set(store, null);
  }
  const startStamp = (store: FuelStore): string => {
    const s = start.get(store);
    return s ? stamp(s.row.date, s.row.time) : '';
  };

  // Running history: ledger rows set the store they touch; refuels take off litres (drums: litres ÷ drum size).
  type Event =
    | { at: string; order: number; kind: 'row'; row: FuelLedgerEntry }
    | { at: string; order: number; kind: 'draw'; entry: DailyEntry };
  const events: Event[] = [
    ...rows.map((row, i): Event => ({ at: stamp(row.date, row.time), order: i, kind: 'row', row })),
    ...draws.map((entry, i): Event => ({
      at: stamp(entry.date, entry.takeoff_time),
      order: rows.length + i,
      kind: 'draw',
      entry,
    })),
  ];
  // At the same time a ledger row goes first, so a refuel timed with a dip is taken off it (TJR's `>=`), and a dip
  // after a resupply (a dip wins a tie).
  const rank = (e: Event): number => (e.kind === 'draw' ? 2 : e.row.kind === 'dip' ? 1 : 0);
  events.sort((a, b) => (a.at !== b.at ? (a.at < b.at ? -1 : 1) : rank(a) - rank(b) || a.order - b.order));

  const used: Balance = zero();
  let bal = zero();
  const history: FuelHistoryRow[] = [];
  const noType: DailyEntry[] = [];
  for (const ev of events) {
    if (ev.kind === 'row') {
      const r = ev.row;
      const next = copy(bal);
      for (const store of FUEL_STORES) {
        if (!touches(r, store)) continue;
        for (const fuel of FUEL_TYPES) next[store][fuel] = ledgerValue(r, store, fuel);
      }
      const uplift: UpliftCheck[] = [];
      if (r.kind === 'resupply')
        for (const store of FUEL_STORES)
          for (const fuel of FUEL_TYPES) {
            const u = upliftValue(r, store, fuel);
            if (u === null) continue;
            const before = bal[store][fuel];
            uplift.push({
              store,
              fuel,
              uplifted: u,
              before,
              expected: before + u,
              recorded: next[store][fuel],
            });
          }
      bal = next;
      history.push({
        key: r.id,
        date: r.date,
        time: r.time,
        kind: r.kind,
        location: r.location,
        balance: copy(bal),
        ...(uplift.length > 0 ? { uplift } : {}),
      });
      continue;
    }
    const e = ev.entry;
    const store = storeOf(e)!;
    const fuel = e.aircraft_id ? (fuelOf.get(e.aircraft_id) ?? null) : null;
    const counts = ev.at >= startStamp(store);
    if (fuel === null) noType.push(e);
    else {
      const drawn = store === 'drums' ? e.fuel_load_l / drumL : e.fuel_load_l;
      bal = copy(bal);
      bal[store][fuel] -= drawn;
      if (counts) used[store][fuel] += e.fuel_load_l;
    }
    history.push({
      key: e.id,
      date: e.date,
      time: e.takeoff_time ?? '',
      kind: 'refuel',
      location: e.fuel_location,
      daily_entry_id: e.id,
      aircraft_id: e.aircraft_id,
      flt_num: e.flt_num,
      store,
      fuel,
      litres: e.fuel_load_l,
      balance: copy(bal),
    });
  }

  const cards: FuelStockCard[] = [];
  const warnings: FuelStockWarning[] = [];
  for (const store of FUEL_STORES) {
    const s = start.get(store) ?? null;
    const mark = (r: FuelLedgerEntry | null | undefined, fuel: FuelType): StockMark | null =>
      r
        ? {
            entry_id: r.id,
            date: r.date,
            time: r.time,
            location: r.location,
            amount: ledgerValue(r, store, fuel),
          }
        : null;
    for (const fuel of FUEL_TYPES) {
      const base = s ? ledgerValue(s.row, store, fuel) : 0;
      const usedL = used[store][fuel];
      const remaining = store === 'drums' ? base - usedL / drumL : base - usedL;
      const inUse = rows.some((r) => ledgerValue(r, store, fuel) > 0) || usedL > 0;
      cards.push({
        store,
        fuel,
        unit: store === 'truck' ? 'L' : 'drums',
        last_fill: mark(fills.get(store), fuel),
        last_dip: mark(dips.get(store), fuel),
        from: s?.from ?? null,
        used_l: usedL,
        remaining,
        remaining_l: store === 'drums' ? remaining * drumL : remaining,
        in_use: inUse,
      });
      const name = `${FUEL_STORE_LABEL[store]} ${FUEL_TYPE_LABEL[fuel]}`;
      if (inUse && s === null) {
        warnings.push({
          code: 'no_stock',
          message: `${name}: refuels are recorded but no ${store === 'truck' ? 'truck' : 'drum'} stock. Add a resupply or a dip.`,
        });
      } else if (remaining < -1e-9) {
        const amount = store === 'drums' ? `${fmtDrums(-remaining)} drums` : `${fmtL(-remaining)} L`;
        warnings.push({
          code: 'below_zero',
          message: `${name} is ${amount} below zero. Record the resupply or a dip, or check the refuel amounts.`,
        });
      }
    }
  }
  if (noType.length > 0) {
    const regos = [...new Set(noType.map((e) => (e.aircraft_id ? (regoOf.get(e.aircraft_id) ?? '') : '')))]
      .map((r) => r || 'no aircraft')
      .join(', ');
    warnings.push({
      code: 'no_fuel_type',
      message: `${noType.length} refuel${noType.length === 1 ? '' : 's'} (${regos}) not taken off the stock: set the aircraft's fuel type in Project settings.`,
    });
  }
  return { drum_l: drumL, cards, history, warnings };
}

// ------------------------------------------------------------------------------------------- per location

/** Locations compare without case or extra spaces ("Mt Clere" = "mt  clere"). */
export function locationKey(location: string): string {
  return location.trim().replace(/\s+/g, ' ').toLowerCase();
}

/** One location's stock (decision 160): the same rule as `fuelStock` on that location's records and refuels. */
export interface LocationStock {
  readonly key: string;
  /** The location as first written in the fuel records. */
  readonly location: string;
  readonly stock: FuelStock;
}

export interface FuelStockByLocation {
  readonly locations: readonly LocationStock[];
  /** Truck and drum refuels whose fuel location no fuel record has (counted in the combined stock only). */
  readonly unplaced: readonly DailyEntry[];
  readonly warnings: readonly FuelStockWarning[];
}

/** Where each truck and drum refuel is drawn from (decision 160), the one rule `fuelStockByLocation` and the TJR
 * export's combined rows use. */
export interface RefuelPlacement {
  /** Location key → the location as first written in the fuel records (oldest first). */
  readonly names: ReadonlyMap<string, string>;
  /** Location key → its refuels. */
  readonly placed: ReadonlyMap<string, readonly DailyEntry[]>;
  readonly unplaced: readonly DailyEntry[];
}

/**
 * Each fuel record's location is a location; a truck or drum refuel goes to the location named by its fuel location,
 * or to the only location when there is one (a refuel with no location, or one no record has), else it is unplaced.
 */
export function refuelPlacement(
  ledger: readonly FuelLedgerEntry[],
  entries: readonly DailyEntry[],
): RefuelPlacement {
  const rows = ledger.filter((r) => r.deleted_at === null);
  const names = new Map<string, string>();
  for (const r of [...rows].sort((a, b) => (stamp(a.date, a.time) < stamp(b.date, b.time) ? -1 : 1))) {
    const k = locationKey(r.location);
    if (!names.has(k)) names.set(k, r.location.trim().replace(/\s+/g, ' '));
  }
  const draws = entries.filter((e) => e.deleted_at === null && storeOf(e) !== null && e.fuel_load_l > 0);
  const only = names.size === 1 ? [...names.keys()][0]! : null;
  const placed = new Map<string, DailyEntry[]>();
  const unplaced: DailyEntry[] = [];
  for (const e of draws) {
    const k = locationKey(e.fuel_location);
    const key = names.has(k) ? k : only;
    if (key === null) unplaced.push(e);
    else placed.set(key, [...(placed.get(key) ?? []), e]);
  }
  return { names, placed, unplaced };
}

/**
 * Fuel stock per store, fuel type and location (MB, decision 160, Q139). Each location gets its own fuel records
 * and the truck and drum refuels with that fuel location; a refuel with no location (or one no record has) goes to
 * the only location when there is one, else it is listed in a warning. The combined figure TJR shows is
 * `fuelStock` over every location.
 */
export function fuelStockByLocation(
  ledger: readonly FuelLedgerEntry[],
  entries: readonly DailyEntry[],
  aircraft: readonly Aircraft[],
  drumL: number = DEFAULT_DRUM_L,
): FuelStockByLocation {
  const rows = ledger.filter((r) => r.deleted_at === null);
  const { names, placed, unplaced } = refuelPlacement(rows, entries);
  const locations: LocationStock[] = [...names.entries()]
    .sort((a, b) => a[1].localeCompare(b[1]))
    .map(([key, location]) => ({
      key,
      location,
      stock: fuelStock(
        rows.filter((r) => locationKey(r.location) === key),
        placed.get(key) ?? [],
        aircraft,
        drumL,
      ),
    }));
  const warnings: FuelStockWarning[] = [];
  if (unplaced.length > 0) {
    const where = [...new Set(unplaced.map((e) => e.fuel_location.trim() || 'no location'))].join(', ');
    warnings.push({
      code: 'no_location',
      message: `${unplaced.length} truck or drum refuel${unplaced.length === 1 ? '' : 's'} (${where}) ${unplaced.length === 1 ? 'matches' : 'match'} no fuel location: counted in the combined stock only. Set the fuel location on the daily entry.`,
    });
  }
  return { locations, unplaced, warnings };
}

/** True when the ledger holds any value for the store an entry refuels from (TJR RS 64621's check). */
export function storeInitialised(ledger: readonly FuelLedgerEntry[], store: FuelStore): boolean {
  return ledger.some((r) => r.deleted_at === null && touches(r, store));
}

/** The store an entry draws from: truck, drums, or null for bowser or no refuel. */
export function refuelStore(e: DailyEntry): FuelStore | null {
  return storeOf(e);
}
